import { useEffect, useMemo, useState } from "react";
import api from "../../../utils/api";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

const WEEKDAYS = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
];

const SLOT_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

export default function CalendarSettingsModal({ open, onClose, settings, onSaved }) {
    const [displayStart, setDisplayStart] = useState("09:00");
    const [displayEnd, setDisplayEnd] = useState("20:00");
    const [nonWorkingDays, setNonWorkingDays] = useState([]);
    const [slotMinutes, setSlotMinutes] = useState(30);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const slotOptions = useMemo(() => {
        const o = [...SLOT_OPTIONS];
        if (!o.includes(slotMinutes)) {
            o.push(slotMinutes);
            o.sort((a, b) => a - b);
        }
        return o;
    }, [slotMinutes]);

    useEffect(() => {
        if (!open || !settings) return;
        setDisplayStart(settings.display_start_time || "09:00");
        setDisplayEnd(settings.display_end_time || "20:00");
        setNonWorkingDays(
            Array.isArray(settings.non_working_days)
                ? settings.non_working_days.map((d) => Number(d))
                : []
        );
        setSlotMinutes(Number(settings.slot_duration_minutes) || 30);
        setError("");
    }, [open, settings]);

    const workingCount = 7 - new Set(nonWorkingDays).size;

    const toggleDay = (dayValue) => {
        setNonWorkingDays((prev) => {
            const set = new Set(prev);
            if (set.has(dayValue)) {
                set.delete(dayValue);
            } else {
                set.add(dayValue);
            }
            return [...set].sort((a, b) => a - b);
        });
    };

    const handleSave = async () => {
        setError("");
        if (workingCount < 1) {
            setError("Keep at least one working day visible on the calendar.");
            return;
        }
        setSaving(true);
        try {
            const res = await api.put("admin/calendar/settings", {
                display_start_time: displayStart,
                display_end_time: displayEnd,
                non_working_days: nonWorkingDays,
                slot_duration_minutes: slotMinutes,
            });
            const data = res.data?.data;
            if (data && onSaved) onSaved(data);
            onClose(true);
        } catch (e) {
            const errs = e.response?.data?.errors;
            const flat =
                errs && typeof errs === "object"
                    ? Object.values(errs)
                          .flat()
                          .filter(Boolean)
                          .join(" ")
                    : "";
            const msg = e.response?.data?.message || flat || e.message || "Could not save settings.";
            setError(String(msg));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Calendar settings</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Working hours define the time range shown in week and day views.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            label="Day starts"
                            type="time"
                            value={displayStart}
                            onChange={(e) => setDisplayStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Day ends"
                            type="time"
                            value={displayEnd}
                            onChange={(e) => setDisplayEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Stack>

                    <FormControl fullWidth>
                        <FormLabel id="slot-duration-label">Time slot length</FormLabel>
                        <Select
                            labelId="slot-duration-label"
                            value={slotMinutes}
                            onChange={(e) => setSlotMinutes(Number(e.target.value))}
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            {slotOptions.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m} minutes ({m >= 60 ? `${m / 60} h` : `${m} min`} grid)
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            Row height in the week/day view (e.g. 30 → 10:00, 10:30, 11:00…).
                        </FormHelperText>
                    </FormControl>

                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">Days shown on calendar</FormLabel>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Uncheck a day to hide it in week/day views (non-working day).
                        </Typography>
                        <FormGroup>
                            {WEEKDAYS.map(({ value, label }) => (
                                <FormControlLabel
                                    key={value}
                                    control={
                                        <Checkbox
                                            checked={!nonWorkingDays.includes(value)}
                                            onChange={() => toggleDay(value)}
                                        />
                                    }
                                    label={label}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>

                    {error ? (
                        <Box sx={{ color: "error.main", typography: "body2" }}>{error}</Box>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => onClose(false)} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={22} color="inherit" /> : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
